import { plainText } from './Inline'
import type { HelpBlock, HelpSection } from './types'

/** URL-safe id for a heading, glossary term or FAQ question ("Two-factor sign-in" → "two-factor-sign-in"). */
export function headingId(text: string): string {
  return plainText(text)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export interface HelpHeading {
  id: string
  text: string
  /** `h` for a sub-heading; `term` and `faq` for glossary and FAQ entries. */
  kind: 'h' | 'term' | 'faq'
}

/** Every heading a section exposes to the filter and the table of contents. */
export function headingsOf(section: HelpSection): HelpHeading[] {
  const headings: HelpHeading[] = []
  for (const block of section.blocks) {
    if (block.type === 'h')
      headings.push({ id: headingId(block.text), text: block.text, kind: 'h' })
    if (block.type === 'terms') {
      for (const item of block.items) {
        headings.push({ id: headingId(item.term), text: item.term, kind: 'term' })
      }
    }
    if (block.type === 'faq') {
      for (const item of block.items)
        headings.push({ id: headingId(item.q), text: item.q, kind: 'faq' })
    }
  }
  return headings
}

function blockText(block: HelpBlock): string {
  switch (block.type) {
    case 'p':
    case 'h':
      return block.text
    case 'list':
    case 'steps':
      return block.items.join(' ')
    case 'seedo':
      return [block.caption ?? '', ...block.rows.flat()].join(' ')
    case 'table':
      return [block.caption ?? '', ...block.head, ...block.rows.flat()].join(' ')
    case 'callout':
      return [block.title ?? '', block.text ?? '', ...(block.items ?? [])].join(' ')
    case 'diagram':
      return ''
    case 'terms':
      return block.items.map((item) => `${item.term} ${item.text}`).join(' ')
    case 'faq':
      return block.items.map((item) => `${item.q} ${item.a}`).join(' ')
  }
}

function normalise(value: string): string {
  return plainText(value).toLowerCase().normalize('NFKD').replace(/\p{M}/gu, '')
}

export interface HelpMatch {
  section: HelpSection
  /** Headings inside the section that match the query (empty without a query). */
  headings: HelpHeading[]
  /** True when the title, summary, keywords or a heading match, not only the body text. */
  inHeadings: boolean
}

/**
 * Filter sections by every word of `query`: section titles, summaries and keywords, then
 * sub-headings, glossary terms and FAQ questions, and finally the body text. Keeps the
 * Help order. An empty query returns every section with no heading matches.
 */
export function filterHelp(sections: readonly HelpSection[], query: string): HelpMatch[] {
  const words = normalise(query).split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return sections.map((section) => ({ section, headings: [], inHeadings: true }))
  }
  const matches: HelpMatch[] = []
  for (const section of sections) {
    const head = normalise([section.title, section.summary, ...(section.keywords ?? [])].join(' '))
    const headings = headingsOf(section).filter((heading) => {
      const text = normalise(heading.text)
      return words.every((word) => text.includes(word))
    })
    const titleHit = words.every((word) => head.includes(word))
    if (titleHit || headings.length > 0) {
      matches.push({ section, headings, inHeadings: true })
      continue
    }
    const body = normalise(section.blocks.map(blockText).join(' '))
    if (words.every((word) => head.includes(word) || body.includes(word))) {
      matches.push({ section, headings: [], inHeadings: false })
    }
  }
  return matches
}
